/**
 * /market/:slug/mesa — landing mobile-first para pedir desde QR de mesa.
 * Search: ?table=&session=&mesa=
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketTableOrderPage } from "@/components/market/MarketTableOrderPage";
import { MarketFunnelTracker } from "@/components/market/MarketFunnelTracker";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@600&display=swap",
};

export type MesaSearch = {
  table: string;
  session: string;
  mesa: string;
  utm_source?: string;
  utm_medium?: string;
};

export const Route = createFileRoute("/market_/$slug/mesa")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  validateSearch: (search: Record<string, unknown>): MesaSearch => {
    const table = typeof search.table === "string" ? search.table : "";
    const session = typeof search.session === "string" ? search.session : "";
    const mesa = typeof search.mesa === "string" ? search.mesa : "Mesa";
    const utm_source = typeof search.utm_source === "string" ? search.utm_source : undefined;
    const utm_medium = typeof search.utm_medium === "string" ? search.utm_medium : undefined;
    return { table, session, mesa, utm_source, utm_medium };
  },
  beforeLoad: ({ params, search }) => {
    if (!search.table || !search.session) {
      throw redirect({
        to: "/market/$slug",
        params: { slug: params.slug },
        replace: true,
      });
    }
  },
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: MesaOrderRoute,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Menú de mesa — ${name}` },
        { name: "description", content: `Pedí desde tu mesa en ${name}` },
        { name: "viewport", content: "width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover" },
        { name: "robots", content: "noindex, nofollow" },
      ],
      links: [{ rel: "manifest", href: `/api/manifest/${params.slug}` }, FONT_LINK],
    };
  },
});

function MesaOrderRoute() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;
  if (!result.ok) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[#0a1620] p-6 text-center text-white">
        <p className="text-sm text-white/70">Menú no disponible.</p>
      </div>
    );
  }

  return (
    <>
      <MarketFunnelTracker slug={slug} channel="qr_mesa" />
      <MarketTableOrderPage
        slug={slug}
        data={result.data}
        ctx={{
          tableToken: search.table,
          sessionId: search.session,
          mesaName: search.mesa || "Mesa",
        }}
      />
    </>
  );
}
